import React from 'react';

const About = () => {
  return (
    <div className="min-h-screen bg-linear-to-b from-gray-900 to-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
            About OLAID
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Empowering your life through instant access to critical medical information and smart financial management.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-20">
          <div className="bg-secondary-dark rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">Our Mission</h2>
            <p className="text-gray-300 mb-6">
              At OLAID, we believe that managing life's essentials should be simple and accessible. Our mission is to provide 
              tools that matter most: protecting your health in emergencies and helping you manage your finances with ease.
            </p>
            <p className="text-gray-300">
              We bridge the critical information gap between patients and first responders while making shared expenses 
              and cash flow tracking stress-free. Two powerful apps, one unified account—your life, simplified.
            </p>
          </div>

          <div className="bg-secondary-dark rounded-lg p-8">
            <h2 className="text-2xl font-semibold text-white mb-4">What Sets Us Apart</h2>
            <ul className="space-y-4 text-gray-300">
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>One account across health and economics platforms</span>
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Innovative QR code technology for instant health data access</span>
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>AI-powered receipt scanning for effortless expense splitting</span>
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Comprehensive cash flow tracking with visual dashboards</span>
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Bilingual support in English and Spanish</span>
              </li>
              <li className="flex items-start">
                <svg className="w-6 h-6 text-primary mr-2 mt-1 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Strong focus on user privacy and data security</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-20">
          <div className="bg-red-500/10 rounded-lg p-8 border border-red-500/30">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-red-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              <h3 className="text-2xl font-semibold text-red-300">Health App</h3>
            </div>
            <p className="text-gray-300 mb-4">
              In medical emergencies, quick access to accurate information can mean the difference between life and death. 
              Our health app ensures that vital medical data is instantly available when first responders need it most.
            </p>
            <p className="text-gray-400">
              Access at: <strong className="text-red-300">app.olaid.me</strong>
            </p>
          </div>

          <div className="bg-green-500/10 rounded-lg p-8 border border-green-500/30">
            <div className="flex items-center mb-4">
              <svg className="w-8 h-8 text-green-400 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-2xl font-semibold text-green-300">Economics App</h3>
            </div>
            <p className="text-gray-300 mb-4">
              Managing shared expenses and personal finances shouldn't be complicated. Our economics app makes it simple 
              to split bills with AI-powered receipt scanning and track your cash flow with intuitive visual tools.
            </p>
            <p className="text-gray-400">
              Access at: <strong className="text-green-300">econ.olaid.me</strong>
            </p>
          </div>
        </div>

        <div className="bg-primary/10 rounded-lg p-8 border border-primary/20 max-w-3xl mx-auto text-center">
          <h3 className="text-2xl font-semibold text-primary-light mb-4">Join Us in Making a Difference</h3>
          <p className="text-gray-300 mb-6">
            Whether you're protecting yourself in emergencies or simplifying your financial life, OLAID is here to help. 
            By choosing OLAID, you're choosing tools that work seamlessly together to make your life easier and safer.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a
              href="https://app.olaid.me/create"
              className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-lg text-lg transition-colors duration-300"
            >
              Get Started Today
            </a>
            <a
              href="https://econ.olaid.me"
              className="bg-secondary hover:bg-secondary-dark text-white px-8 py-4 rounded-lg text-lg transition-colors duration-300 border border-primary/30"
            >
              Explore Economics
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
